/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/fileUpload.scss';

import classNames from 'classnames';
import React, {useMemo, useRef, useState} from 'react';
import {ChangeFunction} from "../util/interfaces";
import {preventDefault} from "../util/miscUtil";
import {stopPropagation, voidFunction} from "../util/controlUtil";
import DvrdButton from "../button/dvrdButton";
import AwesomeIcon from "../icon/awesomeIcon";
import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props {
    onChange: ChangeFunction<FileList>;
    file: File | FileList | null;
    disabled?: boolean;
    className?: string;
    multiple?: boolean;
    accept?: string | Array<string>;
    fileIcon?: IconName;
}

export default function FileUpload(props: Props) {
    const {file, className, disabled, onChange, multiple, fileIcon} = props;
    const [draggingFile, setDraggingFile] = useState(false);
    const dragCounter = useRef(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const accept: string | undefined = useMemo(() => {
        if (!props.accept) return undefined;
        if (Array.isArray(props.accept)) return props.accept.join(',');
        return props.accept;
    }, [props.accept]);
    const buttonLabel = useMemo(() => {
        if (!file) {
            if (multiple) return 'Upload bestanden';
            return 'Upload bestand';
        } else {
            if (multiple) return 'Kies andere bestanden';
            return 'Kies ander bestand';
        }
    }, [file]);

    function _onChange(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files) return;
        onChange(files, evt);
    }

    function onClickUpload() {
        inputRef.current?.click();
    }

    function onDrop(evt: React.DragEvent) {
        preventEvent(evt);
        const {files} = evt.dataTransfer;
        onChange(files);
        evt.dataTransfer.clearData();
        setDraggingFile(false);
        dragCounter.current = 0;
    }

    function onDragEnter(evt: React.DragEvent) {
        preventEvent(evt);
        dragCounter.current += 1;
        setDraggingFile(true);
    }

    function onDragLeave(evt: React.DragEvent) {
        preventEvent(evt);
        if (!draggingFile) return;
        dragCounter.current -= 1;
        if (dragCounter.current <= 0) setDraggingFile(false);
    }

    function preventEvent(evt: React.DragEvent) {
        preventDefault(evt);
        stopPropagation(evt);
    }

    function renderFile() {
        if (file) return renderSelectedFile();
        return renderSelectFile();
    }

    function renderSelectFile() {
        const label = multiple ? 'bestanden' : 'een bestand';
        const icon = fileIcon ?? 'file';
        return (
            <div className='select-file-container'>
                <div className='select-file-label-container'>
                    <AwesomeIcon name={icon} className='file-icon'/>
                    <label className='selected-file-label'>Klik hier om {label} te selecteren, of
                        sleep {label} hierheen.</label>
                </div>
            </div>
        )
    }

    function renderSelectedFile() {
        const text = multiple ? 'Geselecteerde bestanden:' : 'Geselecteerd bestand:';
        const icon = fileIcon ?? 'file';
        return (
            <div className='selected-file-container'>
                <AwesomeIcon name={icon} className='file-icon'/>
                <div className='file-labels-container'>
                    <label className='selected-file-title'>{text}</label>
                    <div className='file-label-container'>
                        {renderFileLabels()}
                    </div>
                </div>
            </div>
        )
    }

    function renderFileLabels() {
        if (!file) return null;
        if (file instanceof FileList) {
            const files = Array.from(file);
            return (
                <>
                    {files.map((file: File, index: number) => (
                        <label key={index} className='file-label'>{file.name}</label>
                    ))}
                </>
            )
        }
        return <label className='file-label'>{file.name}</label>;
    }

    return (
        <div className={classNames('dvrd-file-upload', className, disabled && 'disabled')} onDrag={preventEvent}
             onDragOver={preventEvent} onDrop={onDrop} onDragEnter={onDragEnter} onDragLeave={onDragLeave}
             onClick={onClickUpload}>
            <input type='file' multiple={multiple} accept={accept} ref={inputRef} style={{display: 'none'}}
                   onChange={_onChange}/>
            <div className='file-container'>
                {renderFile()}
            </div>
            <DvrdButton onClick={voidFunction} label={buttonLabel} secondary={!!file}/>
        </div>
    )
}